import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Check,
  Copy,
  Gauge,
  Link2,
  Monitor,
  Palette,
  ShieldCheck,
  Trash2,
  TriangleAlert,
  UserPlus,
  UserRound,
} from "lucide-react";
import { toast } from "../lib/toast";
import type { Invitation, StatsOverview, UserPublic } from "@smsbridge/shared";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Combobox } from "../components/ui/Combobox";
import { PageHeader } from "../components/ui/PageHeader";
import { ThemeSwitcher } from "../components/ui/ThemeSwitcher";
import { FormSkeleton } from "../components/ui/Skeletons";
import { useMinimumDuration } from "../lib/useMinimumDuration";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { ProgressMeter } from "../components/ui/ProgressMeter";
import { api, apiErrorMessage } from "../lib/api";
import { useAuthStore } from "../stores/auth-store";
import { Skeleton } from "../components/ui/Skeleton";

interface SessionRow {
  id: string;
  familyId: string;
  createdAt: string;
  expiresAt: string;
  isCurrent: boolean;
}

/** Intl gives the browser's full zone list; no need to ship a hand-maintained copy. */
function timezoneOptions(): string[] {
  const supported = (
    Intl as unknown as { supportedValuesOf?: (k: string) => string[] }
  ).supportedValuesOf;
  if (typeof supported === "function") return supported("timeZone");
  return [Intl.DateTimeFormat().resolvedOptions().timeZone, "UTC"];
}

const TIMEZONES = timezoneOptions();

function formatWhen(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AccountPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [quietEnabled, setQuietEnabled] = useState(false);
  const [quietStart, setQuietStart] = useState("22:00");
  const [quietEnd, setQuietEnd] = useState("07:00");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const [twoFaOpen, setTwoFaOpen] = useState(false);
  const [twoFaSetup, setTwoFaSetup] = useState<{
    qrCodeDataUrl: string;
    secret: string;
  } | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [copied, setCopied] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteCopied, setInviteCopied] = useState<string | null>(null);

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: async () => (await api.get<UserPublic>("/auth/me")).data,
  });

  // The profile form is seeded from /auth/me by an effect, so without this the inputs render
  // empty for a beat before the real values land.
  const profileLoading = useMinimumDuration(meQuery.isLoading);

  const statsQuery = useQuery({
    queryKey: ["stats", "overview"],
    queryFn: async () => (await api.get<StatsOverview>("/stats/overview")).data,
  });

  const sessionsQuery = useQuery({
    queryKey: ["sessions"],
    queryFn: async () => (await api.get<SessionRow[]>("/auth/sessions")).data,
  });

  // Seed the form once the profile arrives, without clobbering edits in progress afterwards.
  useEffect(() => {
    if (!meQuery.data) return;
    setName(meQuery.data.name);
    setTimezone(meQuery.data.timezone);
    if (meQuery.data.quietHours) {
      setQuietEnabled(true);
      setQuietStart(meQuery.data.quietHours.start);
      setQuietEnd(meQuery.data.quietHours.end);
    }
  }, [meQuery.data]);

  const profileMutation = useMutation({
    mutationFn: async () =>
      (
        await api.patch<UserPublic>("/auth/profile", {
          name,
          timezone,
          quietHours: quietEnabled
            ? { start: quietStart, end: quietEnd }
            : null,
        })
      ).data,
    onSuccess: (updated) => {
      toast.success("Profile saved.");
      useAuthStore.setState({ user: updated });
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "Could not save your profile.")),
  });

  const passwordMutation = useMutation({
    mutationFn: async () =>
      api.post("/auth/change-password", { currentPassword, newPassword }),
    onSuccess: () => {
      // The server revoked every session, including this one — going back to /login is the
      // honest outcome rather than leaving a dead session that 401s on the next click.
      toast.success("Password changed. Please sign in again.");
      useAuthStore.setState({ user: null, status: "anonymous" });
      navigate("/login", { replace: true });
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "Could not change your password.")),
  });

  const revokeSessionMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/auth/sessions/${id}`),
    onSuccess: () => {
      toast.success("Session revoked.");
      void queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "Could not revoke that session.")),
  });

  const setup2faMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ qrCodeDataUrl: string; secret: string }>(
          "/auth/2fa/setup"
        )
      ).data,
    onSuccess: (result) => {
      setTwoFaSetup(result);
      setTwoFaOpen(true);
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "Could not start 2FA setup.")),
  });

  const verify2faMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ recoveryCodes: string[] }>("/auth/2fa/verify", {
          totpCode,
        })
      ).data,
    onSuccess: (result) => {
      setRecoveryCodes(result.recoveryCodes);
      setTotpCode("");
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "That code wasn't accepted.")),
  });

  const disable2faMutation = useMutation({
    mutationFn: async () => api.post("/auth/2fa/disable", { totpCode }),
    onSuccess: () => {
      toast.success("Two-factor authentication disabled.");
      setTotpCode("");
      setTwoFaOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "That code wasn't accepted.")),
  });

  const invitationsQuery = useQuery({
    queryKey: ["invitations"],
    queryFn: async () => (await api.get<Invitation[]>("/invitations")).data,
  });

  const createInviteMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ invitation: Invitation; token: string }>(
          "/invitations",
          {
            email: inviteEmail.trim() || undefined,
          }
        )
      ).data,
    onSuccess: async (data) => {
      const url = `${window.location.origin}/invite/${data.token}`;
      await navigator.clipboard.writeText(url);
      setInviteCopied(data.invitation._id);
      setInviteEmail("");
      toast.success("Invite link copied to clipboard.");
      void queryClient.invalidateQueries({ queryKey: ["invitations"] });
      setTimeout(() => setInviteCopied(null), 3000);
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "Could not create invite.")),
  });

  const revokeInviteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/invitations/${id}`),
    onSuccess: () => {
      toast.success("Invite revoked.");
      void queryClient.invalidateQueries({ queryKey: ["invitations"] });
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "Could not revoke invite.")),
  });

  const deleteMutation = useMutation({
    mutationFn: async () =>
      api.delete("/auth/account", { data: { password: deletePassword } }),
    onSuccess: () => {
      toast.success("Account deleted.");
      useAuthStore.setState({ user: null, status: "anonymous" });
      navigate("/login", { replace: true });
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "Could not delete the account.")),
  });

  const mfaEnabled = meQuery.data?.mfaEnabled ?? false;
  const stats = statsQuery.data;

  async function copyCodes() {
    if (!recoveryCodes) return;
    await navigator.clipboard.writeText(recoveryCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-8">
      <PageHeader
        sticky
        title="Account"
        description="Your profile, security settings and plan usage."
      />

      {/* Profile */}
      <Card padding="lg" className="flex flex-col gap-4">
        <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-text">
          <UserRound className="size-5 text-text-muted" aria-hidden />
          Profile
        </h2>

        {profileLoading ? (
          <FormSkeleton fields={3} />
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              profileMutation.mutate();
            }}
          >
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <Input
              label="Email"
              value={meQuery.data?.email ?? ""}
              disabled
              helperText="Email cannot be changed."
            />

            {/* ~420 IANA zones. The value is still a plain string in the same `timezone`
              state, so submission is byte-for-byte what it was with the native select. */}
            <Combobox
              id="tz"
              label="Timezone"
              value={timezone}
              onChange={setTimezone}
              options={TIMEZONES}
              placeholder="Select a timezone"
              searchPlaceholder="Type to filter, e.g. Karachi"
              emptyMessage="No timezone matches that."
              helperText="Sets when your daily quota resets and when quiet hours apply."
            />

            <div className="flex flex-col gap-2">
              <label className="flex cursor-pointer items-center gap-2.5">
                <input
                  type="checkbox"
                  checked={quietEnabled}
                  onChange={(e) => setQuietEnabled(e.target.checked)}
                  className="size-4 accent-accent"
                />
                <span className="text-sm font-medium text-text">
                  Quiet hours
                </span>
              </label>
              <p className="text-xs text-text-muted">
                Messages scheduled inside this window are held until it ends.
              </p>
              {quietEnabled && (
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="quiet-start"
                      className="text-sm text-text-muted"
                    >
                      From
                    </label>
                    <input
                      id="quiet-start"
                      type="time"
                      value={quietStart}
                      onChange={(e) => setQuietStart(e.target.value)}
                      className="h-10 rounded-control border border-border bg-surface px-3 text-sm text-text outline-none transition-colors duration-fast ease-standard focus:border-accent focus:ring-2 focus:ring-accent/20"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="quiet-end"
                      className="text-sm text-text-muted"
                    >
                      To
                    </label>
                    <input
                      id="quiet-end"
                      type="time"
                      value={quietEnd}
                      onChange={(e) => setQuietEnd(e.target.value)}
                      className="h-10 rounded-control border border-border bg-surface px-3 text-sm text-text outline-none transition-colors duration-fast ease-standard focus:border-accent focus:ring-2 focus:ring-accent/20"
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <Button type="submit" loading={profileMutation.isPending}>
                Save profile
              </Button>
            </div>
          </form>
        )}
      </Card>

      {/* Appearance */}
      <Card padding="lg" className="flex flex-col gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-text">
            <Palette className="size-5 text-text-muted" aria-hidden />
            Appearance
          </h2>
          <p className="mt-1 text-sm text-text-muted">
            Choose how SMS Fort looks. System follows your device setting and
            updates automatically when it changes.
          </p>
        </div>
        <ThemeSwitcher />
      </Card>

      {/* Plan & usage */}
      <Card padding="lg" className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-text">
            <Gauge className="size-5 text-text-muted" aria-hidden />
            Plan &amp; usage
          </h2>
          {meQuery.data?.emailVerified ? (
            <Badge tone="success">Email verified</Badge>
          ) : (
            <Badge tone="warn">Email not verified</Badge>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-text">Today</span>
              <span className="text-text-muted">
                {stats ? `${stats.today.used} / ${stats.today.limit}` : "—"}
              </span>
            </div>
            <ProgressMeter
              value={stats?.today.used ?? 0}
              max={stats?.today.limit ?? 1}
            />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-text">Last 30 days</span>
              <span className="text-text-muted">
                {stats
                  ? `${stats.last30Days.used} / ${stats.last30Days.limit}`
                  : "—"}
              </span>
            </div>
            <ProgressMeter
              value={stats?.last30Days.used ?? 0}
              max={stats?.last30Days.limit ?? 1}
            />
          </div>

          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ["Sent", stats?.totalSent],
              ["Received", stats?.totalReceived],
              ["Devices", stats?.deviceCount],
              ["Active keys", stats?.activeApiKeyCount],
            ].map(([label, value]) => (
              <div
                key={label as string}
                className="rounded-control bg-bg px-3 py-2"
              >
                <dt className="text-xs text-text-muted">{label}</dt>
                <dd className="text-lg font-semibold text-text">
                  {value ?? "—"}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </Card>

      {/* Security */}
      <Card padding="lg" className="flex flex-col gap-5">
        <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-text">
          <ShieldCheck className="size-5 text-text-muted" aria-hidden />
          Security
        </h2>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            passwordMutation.mutate();
          }}
        >
          <p className="text-sm font-medium text-text">Change password</p>
          <Input
            label="Current password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="At least 10 characters. Changing it signs out every device."
          />
          <div>
            <Button
              type="submit"
              variant="secondary"
              loading={passwordMutation.isPending}
              disabled={currentPassword === "" || newPassword.length < 10}
            >
              Change password
            </Button>
          </div>
        </form>

        <div className="border-t border-border pt-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 text-sm font-medium text-text">
                <ShieldCheck className="size-4" aria-hidden />
                Two-factor authentication
                {mfaEnabled ? (
                  <Badge tone="success">On</Badge>
                ) : (
                  <Badge tone="neutral">Off</Badge>
                )}
              </p>
              <p className="mt-1 text-sm text-text-muted">
                Require a code from your authenticator app at sign-in.
              </p>
            </div>
            {mfaEnabled ? (
              <Button variant="secondary" onClick={() => setTwoFaOpen(true)}>
                Manage
              </Button>
            ) : (
              <Button
                variant="secondary"
                loading={setup2faMutation.isPending}
                onClick={() => setup2faMutation.mutate()}
              >
                Enable
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Sessions */}
      <Card padding="lg" className="flex flex-col gap-4">
        <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-text">
          <Monitor className="size-5 text-text-muted" aria-hidden />
          Active sessions
        </h2>
        {sessionsQuery.isLoading && (
          <div className="flex flex-col gap-2">
            <Skeleton variant="text" className="w-2/3" />
            <Skeleton variant="text" className="w-1/2" />
          </div>
        )}
        {(sessionsQuery.data ?? []).length === 0 &&
          !sessionsQuery.isLoading && (
            <p className="text-sm text-text-muted">No other active sessions.</p>
          )}
        {/*
          Capped and scrollable: sessions accumulate one row per sign-in, and an uncapped list
          pushes every section below it — Invite a user, What is new, Danger zone — far off the
          page. 22rem shows roughly four rows whole and clips the fifth, which is what tells the
          reader there is more without needing a visible bar.

          no-scrollbar rather than scrollbar-slim: a permanent gutter inside a card this narrow
          reads as clutter, and the half-clipped row is already the affordance. The list stays
          scrollable by wheel, trackpad, touch and keyboard.
        */}
        <ul className="no-scrollbar flex max-h-[22rem] flex-col gap-2 overflow-y-auto overscroll-contain">
          {(sessionsQuery.data ?? []).map((session) => (
            <li
              key={session.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-control border border-border px-3 py-2.5"
            >
              <div className="flex items-center gap-3">
                <Monitor className="size-4 text-text-muted" aria-hidden />
                <div>
                  <p className="text-sm text-text">
                    Signed in {formatWhen(session.createdAt)}
                    {session.isCurrent && (
                      <Badge tone="accent" className="ml-2">
                        This device
                      </Badge>
                    )}
                  </p>
                  <p className="text-xs text-text-muted">
                    Expires {formatWhen(session.expiresAt)}
                  </p>
                </div>
              </div>
              {!session.isCurrent && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-danger hover:bg-danger-soft"
                  loading={
                    revokeSessionMutation.isPending &&
                    revokeSessionMutation.variables === session.id
                  }
                  onClick={() => revokeSessionMutation.mutate(session.id)}
                >
                  Revoke
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Card>

      {/* Invitations */}
      <Card padding="lg" className="flex flex-col gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-text">
            <UserPlus className="size-5 text-text-muted" aria-hidden />
            Invite a user
          </h2>
          <p className="mt-1 text-sm text-text-muted">
            Generate a one-time invite link. Leave the email blank for an open
            link anyone can use.
          </p>
        </div>

        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            createInviteMutation.mutate();
          }}
        >
          <div className="flex-1 min-w-48">
            <Input
              label="Email (optional)"
              type="email"
              placeholder="colleague@company.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
          </div>
          <Button
            type="submit"
            variant="secondary"
            leftIcon={<Link2 className="size-4" aria-hidden />}
            loading={createInviteMutation.isPending}
          >
            Create &amp; copy link
          </Button>
        </form>

        {(invitationsQuery.data ?? []).length > 0 && (
          <ul className="flex flex-col gap-2">
            {(invitationsQuery.data ?? []).map((inv) => (
              <li
                key={inv._id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-control border border-border px-3 py-2.5"
              >
                <div>
                  <p className="text-sm text-text">
                    {inv.email ?? (
                      <span className="text-text-muted italic">Open link</span>
                    )}
                    {inv.usedAt && (
                      <span className="ml-2 text-xs text-text-muted">
                        Used by {inv.usedByEmail}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-text-muted">
                    Expires {new Date(inv.expiresAt).toLocaleDateString()}
                  </p>
                </div>
                {!inv.usedAt && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-danger hover:bg-danger-soft"
                    loading={
                      revokeInviteMutation.isPending &&
                      revokeInviteMutation.variables === inv._id
                    }
                    leftIcon={<Trash2 className="size-3.5" aria-hidden />}
                    onClick={() => revokeInviteMutation.mutate(inv._id)}
                  >
                    Revoke
                  </Button>
                )}
                {inviteCopied === inv._id && (
                  <span className="flex items-center gap-1 text-xs text-success">
                    <Check className="size-3.5" aria-hidden /> Copied
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Danger zone */}
      <Card padding="lg" className="flex flex-col gap-3 border-danger/40">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-danger">
          <TriangleAlert className="size-5" aria-hidden />
          Danger zone
        </h2>
        <p className="text-sm text-text-muted">
          Deleting your account removes every device, message, contact, API key
          and webhook. This cannot be undone.
        </p>
        <div>
          <Button variant="danger" onClick={() => setDeleteOpen(true)}>
            Delete account
          </Button>
        </div>
      </Card>

      {/* 2FA modal */}
      <Modal
        open={twoFaOpen}
        onClose={() => {
          setTwoFaOpen(false);
          setRecoveryCodes(null);
          setTwoFaSetup(null);
          setTotpCode("");
        }}
        title={
          mfaEnabled
            ? "Two-factor authentication"
            : "Set up two-factor authentication"
        }
        description={
          mfaEnabled
            ? "Enter a current code to turn it off."
            : "Scan this with your authenticator app, then enter the six-digit code it shows."
        }
      >
        <div className="flex flex-col gap-4">
          {recoveryCodes ? (
            <>
              <p className="text-sm text-text">
                Save these recovery codes somewhere safe. Each one works once if
                you lose your phone.
              </p>
              <pre className="grid grid-cols-2 gap-1 rounded-control border border-border bg-bg p-3 text-sm text-text">
                {recoveryCodes.map((code) => (
                  <span key={code}>{code}</span>
                ))}
              </pre>
              <Button
                leftIcon={
                  copied ? (
                    <Check className="size-4" aria-hidden />
                  ) : (
                    <Copy className="size-4" aria-hidden />
                  )
                }
                onClick={() => void copyCodes()}
              >
                {copied ? "Copied" : "Copy codes"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setTwoFaOpen(false);
                  setRecoveryCodes(null);
                  setTwoFaSetup(null);
                }}
              >
                Done
              </Button>
            </>
          ) : (
            <>
              {!mfaEnabled && twoFaSetup && (
                <div className="flex flex-col items-center gap-3">
                  <img
                    src={twoFaSetup.qrCodeDataUrl}
                    alt="Two-factor setup QR code"
                    className="size-48 rounded-card bg-white p-2"
                  />
                  <code className="break-all text-center text-xs text-text-muted">
                    {twoFaSetup.secret}
                  </code>
                </div>
              )}

              <Input
                label="Six-digit code"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
              />

              <Button
                loading={
                  verify2faMutation.isPending || disable2faMutation.isPending
                }
                disabled={totpCode.length !== 6}
                onClick={() =>
                  mfaEnabled
                    ? disable2faMutation.mutate()
                    : verify2faMutation.mutate()
                }
              >
                {mfaEnabled ? "Disable 2FA" : "Verify and enable"}
              </Button>
            </>
          )}
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete your account?"
        description="Everything is removed permanently. Enter your password to confirm."
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              disabled={deletePassword === ""}
              onClick={() => deleteMutation.mutate()}
            >
              Delete permanently
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
}
